import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import { Box, Plus, Loader2, RotateCcw, CheckCircle2, X, KeyRound, FileSignature, FileText, RefreshCw } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });
const fmtDate = (v) => {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1].slice(2)}` : String(v);
};
const money = (v) => (v === null || v === undefined || v === '') ? '—' : `$${Number(v).toFixed(2)}`;

// Drawn-signature pad (DPR-scaled so ink tracks the pointer).
const SignaturePad = ({ onChange }) => {
  const ref = useRef(null); const drawing = useRef(false); const inked = useRef(false);
  useEffect(() => {
    const c = ref.current; const ctx = c.getContext('2d');
    const setup = () => { const r = c.getBoundingClientRect(); const dpr = window.devicePixelRatio || 1; c.width = Math.max(1, Math.round(r.width * dpr)); c.height = Math.max(1, Math.round(r.height * dpr)); ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.strokeStyle = '#0E2A47'; };
    setup();
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return { x: t.clientX - r.left, y: t.clientY - r.top }; };
    const start = (e) => { drawing.current = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); e.preventDefault(); };
    const move = (e) => { if (!drawing.current) return; const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); inked.current = true; e.preventDefault(); };
    const end = () => { if (drawing.current) { drawing.current = false; onChange(inked.current ? c.toDataURL('image/png') : ''); } };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move); window.addEventListener('mouseup', end);
    c.addEventListener('touchstart', start, { passive: false }); c.addEventListener('touchmove', move, { passive: false }); c.addEventListener('touchend', end);
    return () => { c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move); window.removeEventListener('mouseup', end); c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move); c.removeEventListener('touchend', end); };
  }, [onChange]);
  const clear = () => { const c = ref.current; const ctx = c.getContext('2d'); ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, c.width, c.height); ctx.restore(); inked.current = false; onChange(''); };
  return (
    <div>
      <canvas ref={ref} className="h-24 w-full cursor-crosshair rounded-lg border border-slate-300 bg-white" data-testid="prop-ack-pad" />
      <button type="button" onClick={clear} className="mt-1 text-[11px] font-semibold text-slate-400 underline" data-testid="prop-ack-clear">Clear signature</button>
    </div>
  );
};

const STATUS_TONE = {
  Outstanding: 'bg-amber-100 text-amber-800',
  'Partially Returned': 'bg-blue-100 text-blue-800',
  Returned: 'bg-emerald-100 text-emerald-800',
  'Lost / Not Returned': 'bg-rose-100 text-rose-700',
};

const Row = ({ it, canManage, onReturn, onAck, onReplace }) => {
  const [open, setOpen] = useState(false);
  const [r, setR] = useState({ reason: 'Lost / replaced', quantity: 1, date: '', new_identifier: '' });
  const [busy, setBusy] = useState(false);
  const hist = it.replacement_history || [];
  const save = async () => { setBusy(true); await onReplace(it, r); setBusy(false); setOpen(false); setR({ reason: 'Lost / replaced', quantity: 1, date: '', new_identifier: '' }); };
  return (
    <div className="border-t border-slate-100 py-2 text-sm first:border-0" data-testid={`prop-row-${it.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-[#0E2A47]">{it.category}</span>
            <span className="text-slate-500">· {it.item_type}{it.identifier ? ` (${it.identifier})` : ''}</span>
            {it.legacy && <span className="rounded bg-slate-100 px-1 py-0.5 text-[9px] font-semibold uppercase text-slate-400">{it.source}</span>}
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            Issued {fmtDate(it.issued_date)} · Qty {it.quantity_issued}
            {it.size ? ` · Size ${it.size}` : ''}
            {(it.unit_replacement_value !== null && it.unit_replacement_value !== undefined) ? ` · Repl. ${money(it.unit_replacement_value)}` : ''}
            {it.outstanding > 0 ? ` · ${it.outstanding} outstanding` : ''}
            {it.location_name ? ` · ${it.location_name}` : ''}
            {it.acknowledged ? ' · Acknowledged' : ''}
          </div>
          {hist.length > 0 && (
            <div className="mt-1 rounded bg-slate-50 px-2 py-1 text-[10px] text-slate-500" data-testid={`prop-replhist-${it.id}`}>
              {hist.map((h, i) => <div key={i}>↻ {fmtDate(h.date)} · {h.reason}{h.quantity > 1 ? ` ×${h.quantity}` : ''}{h.new_identifier ? ` → ${h.new_identifier}` : ''} <span className="text-slate-400">({h.by})</span></div>)}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_TONE[it.return_status] || 'bg-slate-100 text-slate-500'}`}>{it.return_status || '—'}</span>
          {canManage && !it.legacy && (
            <div className="flex flex-wrap justify-end gap-1">
              {!it.acknowledged && it.outstanding > 0 && <button onClick={() => onAck(it)} className="inline-flex items-center gap-0.5 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100" data-testid={`prop-ack-${it.id}`}><CheckCircle2 size={10} />Ack</button>}
              {it.outstanding > 0 && <button onClick={() => onReturn(it)} className="inline-flex items-center gap-0.5 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100" data-testid={`prop-return-${it.id}`}><RotateCcw size={10} />Return</button>}
              <button onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-0.5 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid={`prop-replace-${it.id}`}><RefreshCw size={10} />Replace</button>
            </div>
          )}
        </div>
      </div>
      {open && (
        <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-2" data-testid={`prop-replace-form-${it.id}`}>
          <label className="text-[10px] font-semibold uppercase text-slate-500">Reason
            <input value={r.reason} onChange={(e) => setR((p) => ({ ...p, reason: e.target.value }))} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal" data-testid={`prop-replace-reason-${it.id}`} /></label>
          <label className="text-[10px] font-semibold uppercase text-slate-500">Date
            <input type="date" value={r.date} onChange={(e) => setR((p) => ({ ...p, date: e.target.value }))} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal" data-testid={`prop-replace-date-${it.id}`} /></label>
          <label className="text-[10px] font-semibold uppercase text-slate-500">Qty
            <input type="number" min="1" value={r.quantity} onChange={(e) => setR((p) => ({ ...p, quantity: e.target.value }))} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal" data-testid={`prop-replace-qty-${it.id}`} /></label>
          <label className="text-[10px] font-semibold uppercase text-slate-500">New identifier (opt)
            <input value={r.new_identifier} onChange={(e) => setR((p) => ({ ...p, new_identifier: e.target.value }))} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal" data-testid={`prop-replace-newid-${it.id}`} /></label>
          <div className="col-span-2 flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="rounded border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-600">Cancel</button>
            <button onClick={save} disabled={busy} className="rounded bg-[#1763B6] px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50" data-testid={`prop-replace-save-${it.id}`}>{busy ? 'Saving…' : 'Record replacement'}</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AddForm = ({ meta, locations, onAdd, onClose, busy }) => {
  const [f, setF] = useState({ category: meta.categories?.[2] || 'Key', item_type: '', identifier: '', size: '', unit_replacement_value: '', quantity_issued: 1, issued_date: '', service_location_id: '', acknowledged: false, notes: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const rvMatch = (meta.replacement_values || []).find((r) => r.label.toLowerCase() === (f.item_type || '').trim().toLowerCase());
  const rvHint = rvMatch && rvMatch.value !== null ? `Default ${money(rvMatch.value)}` : '';
  const isUniform = f.category === 'Uniform';
  return (
    <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="prop-add-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-1 text-[10px] font-semibold uppercase text-slate-500">Type
          <select value={f.category} onChange={(e) => set('category', e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-category">
            {(meta.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="col-span-1 text-[10px] font-semibold uppercase text-slate-500">Item / description
          <input list="prop-item-list" value={f.item_type} onChange={(e) => set('item_type', e.target.value)} placeholder={isUniform ? 'TFS Polo, TFS T-shirt…' : 'Key, Building Access Card…'} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-item" />
          <datalist id="prop-item-list">{(isUniform ? (meta.uniform_types || []) : (meta.replacement_values || []).map((r) => r.label)).map((t) => <option key={t} value={t} />)}</datalist>
        </label>
        {isUniform && <label className="col-span-1 text-[10px] font-semibold uppercase text-slate-500">Size
          <select value={f.size} onChange={(e) => set('size', e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-size">
            <option value="">—</option>{(meta.uniform_sizes || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>}
        <label className="col-span-1 text-[10px] font-semibold uppercase text-slate-500">Identifier (opt)
          <input value={f.identifier} onChange={(e) => set('identifier', e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-identifier" />
        </label>
        <div className="grid grid-cols-3 gap-2 col-span-2">
          <label className="text-[10px] font-semibold uppercase text-slate-500">Qty
            <input type="number" min="1" value={f.quantity_issued} onChange={(e) => set('quantity_issued', e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-qty" />
          </label>
          <label className="text-[10px] font-semibold uppercase text-slate-500">Issued
            <input type="date" value={f.issued_date} onChange={(e) => set('issued_date', e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-date" />
          </label>
          <label className="text-[10px] font-semibold uppercase text-slate-500">Repl. value {rvHint && <span className="text-emerald-600">· {rvHint}</span>}
            <input type="number" step="0.01" min="0" value={f.unit_replacement_value} onChange={(e) => set('unit_replacement_value', e.target.value)} placeholder={rvMatch && rvMatch.value !== null ? String(rvMatch.value) : '0.00'} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-value" />
          </label>
        </div>
      </div>
      <label className="mt-2 block text-[10px] font-semibold uppercase text-slate-500">Service location (opt)
        <select value={f.service_location_id} onChange={(e) => set('service_location_id', e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1 text-xs font-normal text-slate-700" data-testid="prop-add-location">
          <option value="">— Not location-specific —</option>
          {(locations || []).map((l) => <option key={l.id} value={l.id}>{l.location_name}{l.customer_name ? ` · ${l.customer_name}` : ''}</option>)}
        </select>
      </label>
      <p className="mt-1 text-[10px] text-slate-400">Replacement values are references only — issuing an item never creates a charge.</p>
      <div className="mt-2 flex gap-2">
        <button disabled={busy || !f.item_type.trim()} onClick={() => onAdd(f)} className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1763B6] disabled:opacity-50" data-testid="prop-add-save">{busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}Record</button>
        <button onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100" data-testid="prop-add-cancel">Cancel</button>
      </div>
    </div>
  );
};

const AckModal = ({ items, token, personnelId, onDone, onClose }) => {
  const [sel, setSel] = useState(() => new Set(items.map((i) => i.id)));
  const [name, setName] = useState('');
  const [sig, setSig] = useState('');
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const chosen = items.filter((i) => sel.has(i.id));
  const total = chosen.reduce((t, i) => t + (Number(i.unit_replacement_value) || 0) * (Number(i.quantity_issued) || 1), 0);
  const submit = async () => {
    if (!chosen.length) return toast.error('Select at least one item');
    if (!name.trim()) return toast.error('Type the legal name');
    if (!sig) return toast.error('Please draw a signature');
    setBusy(true);
    try {
      await axios.post(`${API}/property-register/personnel/${personnelId}/acknowledgment`, { item_ids: chosen.map((i) => i.id), typed_legal_name: name.trim(), signature_png: sig }, auth(token));
      toast.success('Acknowledgment signed'); onDone(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not sign acknowledgment'); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="prop-ack-modal">
      <div className="max-h-[90vh] w-full max-w-lg overflow-auto rounded-2xl bg-white p-5 shadow-xl">
        <div className="mb-2 flex items-center justify-between"><div className="text-sm font-bold text-[#0E2A47]">Property Acknowledgment</div><button onClick={onClose} className="text-slate-400"><X size={18} /></button></div>
        <p className="mb-2 text-[11px] text-slate-500">Confirm the issued items you received. Replacement values are references only — not a charge or deduction.</p>
        <div className="mb-2 max-h-40 overflow-auto rounded-lg border border-slate-200">
          {items.map((i) => (
            <label key={i.id} className="flex items-center justify-between gap-2 border-t border-slate-100 px-2 py-1.5 text-xs first:border-0" data-testid={`prop-ack-item-${i.id}`}>
              <span className="flex items-center gap-2"><input type="checkbox" checked={sel.has(i.id)} onChange={() => toggle(i.id)} />{i.category} · {i.item_type}{i.size ? ` (${i.size})` : ''} ×{i.quantity_issued}</span>
              <span className="text-slate-500">{money(i.unit_replacement_value)}</span>
            </label>
          ))}
        </div>
        <div className="mb-2 flex justify-between text-xs font-semibold text-[#0E2A47]"><span>Total reference value</span><span data-testid="prop-ack-total">{money(total)}</span></div>
        <label className="block text-[10px] font-semibold uppercase text-slate-500">Typed legal name
          <input value={name} onChange={(e) => setName(e.target.value)} className="mt-0.5 w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-normal" data-testid="prop-ack-name" /></label>
        <div className="mt-2 text-[10px] font-semibold uppercase text-slate-500">Signature</div>
        <SignaturePad onChange={setSig} />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-1 rounded-lg bg-[#1763B6] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50" data-testid="prop-ack-submit">{busy ? <Loader2 size={12} className="animate-spin" /> : <FileSignature size={12} />}Sign &amp; save</button>
        </div>
      </div>
    </div>
  );
};

export const PropertyRegisterSection = ({ personnelId }) => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState({ categories: [] });
  const [acks, setAcks] = useState([]);
  const [adding, setAdding] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [d, m, a] = await Promise.all([
        axios.get(`${API}/property-register/personnel/${personnelId}`, auth(token)),
        axios.get(`${API}/property-register/meta`, auth(token)),
        axios.get(`${API}/property-register/personnel/${personnelId}/acknowledgments`, auth(token)),
      ]);
      setData(d.data); setMeta(m.data); setAcks(a.data.acknowledgments || []);
    } catch (_) { setData({ items: [], legacy_items: [], can_manage: false, summary: {} }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [personnelId]);

  const openAckPdf = async (id) => {
    try {
      const r = await axios.get(`${API}/property-register/acknowledgment/${id}/pdf`, { ...auth(token), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (_) { toast.error('Could not open the acknowledgment PDF'); }
  };

  const add = async (f) => {
    setBusy(true);
    try {
      await axios.post(`${API}/property-register/personnel/${personnelId}`, {
        category: f.category, item_type: f.item_type, identifier: f.identifier || null,
        size: f.size || null, unit_replacement_value: f.unit_replacement_value === '' ? null : Number(f.unit_replacement_value),
        quantity_issued: Number(f.quantity_issued) || 1, issued_date: f.issued_date || null,
        service_location_id: f.service_location_id || null,
        acknowledged: f.acknowledged, notes: f.notes || null,
      }, auth(token));
      setAdding(false); await load();
      toast.success('Property item issued');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not issue the item'); }
    setBusy(false);
  };
  const recordReturn = async (it) => {
    try {
      await axios.patch(`${API}/property-register/${it.id}`, {
        quantity_returned: it.quantity_issued, return_status: 'Returned', returned_date: new Date().toISOString().slice(0, 10),
      }, auth(token));
      await load();
      toast.success('Marked as returned');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record the return'); }
  };
  const acknowledge = async (it) => {
    try {
      await axios.patch(`${API}/property-register/${it.id}`, { acknowledged: true, acknowledged_method: 'In person' }, auth(token));
      await load();
      toast.success('Acknowledgement recorded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record acknowledgement'); }
  };

  const recordReplacement = async (it, r) => {
    try {
      await axios.post(`${API}/property-register/${it.id}/replacement`, {
        reason: r.reason || 'Replacement', quantity: Number(r.quantity) || 1,
        date: r.date || null, new_identifier: r.new_identifier || null,
      }, auth(token));
      await load();
      toast.success('Replacement recorded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record replacement'); }
  };

  const items = data?.items || [];
  const legacy = data?.legacy_items || [];
  const all = [...items, ...legacy];
  const canManage = !!data?.can_manage;
  const s = data?.summary || {};

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="pc-section-property-register">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-bold text-[#0E2A47]"><Box size={16} className="text-[#1763B6]" />Property, Keys &amp; Access Register</div>
        <div className="flex items-center gap-2">
          {(s.outstanding_items || 0) > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800" data-testid="prop-outstanding-count">{s.outstanding_items} outstanding</span>}
          {canManage && (items.length > 0) && <button onClick={() => setAckOpen(true)} className="inline-flex items-center gap-1 rounded-lg border border-[#1763B6] px-2.5 py-1 text-[11px] font-semibold text-[#1763B6] hover:bg-blue-50" data-testid="prop-ack-btn"><FileSignature size={12} />Sign acknowledgment</button>}
          {canManage && !adding && <button onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded-lg bg-[#0E2A47] px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-[#1763B6]" data-testid="prop-add-btn"><Plus size={12} />Issue</button>}
        </div>
      </div>
      {ackOpen && <AckModal items={items} token={token} personnelId={personnelId} onDone={load} onClose={() => setAckOpen(false)} />}
      {!data && <div className="flex items-center gap-2 py-4 text-xs text-slate-400"><Loader2 size={13} className="animate-spin" />Loading register…</div>}
      {data && (
        <>
          {adding && <AddForm meta={meta} locations={data?.assignable_locations || []} onAdd={add} onClose={() => setAdding(false)} busy={busy} />}
          {all.length === 0
            ? <p className="flex items-center gap-1.5 text-xs text-slate-400" data-testid="prop-empty"><KeyRound size={13} />No property, keys or access items issued.</p>
            : all.map((it) => <Row key={it.id} it={it} canManage={canManage} onReturn={recordReturn} onAck={acknowledge} onReplace={recordReplacement} />)}
          {legacy.length > 0 && <p className="mt-2 text-[10px] text-slate-400">Uniform &amp; Company ID records are shown from their authoritative stores.</p>}
          {acks.length > 0 && (
            <div className="mt-3 border-t border-slate-100 pt-2" data-testid="prop-ack-list">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Signed acknowledgments</div>
              {acks.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-1 text-xs" data-testid={`prop-ack-row-${a.id}`}>
                  <span className="text-slate-600">{fmtDate(a.signed_at)} · {a.typed_legal_name} · {money(a.total_reference_value)} · {(a.items || []).length} item(s)</span>
                  <button type="button" onClick={() => openAckPdf(a.id)} className="inline-flex items-center gap-1 font-semibold text-[#1763B6] hover:underline" data-testid={`prop-ack-pdf-${a.id}`}><FileText size={12} />View PDF</button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PropertyRegisterSection;
